<template>
	<div class="bibliotheque h-100" v-touch:swipe.right="goParent">
		<div class="block">
			<div class="block-content block-content-full">
				<div class="input-group">
					<input type="search" class="form-control" v-model="query"
						   :placeholder="$t('Search a file or folder')" @input="onSearchInput">
					<div class="input-group-append">
						<button class="btn btn-secondary" type="button" @click="resetSearch" v-if="isSearch">
							<i class="fa fa-times"></i>
						</button>
						<span class="input-group-text" v-else><i class="fa fa-search"></i></span>
						<button class="btn btn-secondary" type="button" @click="reload"
								:title="$t('Refresh')">
							<i class="fa fa-sync-alt"></i>
						</button>
					</div>
				</div>
			</div>
		</div>

		<div class="text-center py-4" v-if="loading">
			<i class="fa fa-2x fa-circle-notch fa-spin text-muted"></i>
		</div>

		<template v-else>
			<div class="alert alert-warning" v-if="error">{{ $t('Library unavailable') }}</div>

			<template v-else-if="isSearch">
				<div class="font-size-sm text-muted px-2 mb-2">{{ search.count }} {{ $t('result(s)') }}</div>
				<ul class="list-group" v-if="search.count">
					<a href="#" v-for="dos in search.dossiers" :key="`s-dos-`+dos.id"
					   class="list-group-item list-group-item-action d-flex"
					   @click.prevent="openFolder(dos.id)">
						<div><display-extension></display-extension></div>
						<div class="ml-3 flex-fill">
							{{ dos.libelle }}
							<div class="font-size-sm text-muted" v-if="dos.breadcrumb">{{ dos.breadcrumb }}</div>
						</div>
					</a>
					<a href="#" v-for="fic in search.fichiers" :key="`s-fic-`+fic.id"
					   class="list-group-item list-group-item-action d-flex"
					   :class="{'disabled': isDownloading(fic.id)}"
					   @click.prevent="download(fic)">
						<div>
							<i class="fa fa-circle-notch fa-spin text-muted" v-if="isDownloading(fic.id)"></i>
							<display-extension :extension="fic.extension" v-else></display-extension>
						</div>
						<div class="ml-3 flex-fill">
							{{ fic.libelle }}
							<div class="font-size-sm text-muted">{{ fic.taillehumaine }}</div>
						</div>
					</a>
				</ul>
				<div class="text-center text-muted py-4" v-else>{{ $t('No result') }}</div>
			</template>

			<template v-else-if="content">
				<div class="d-flex align-items-center px-2 mb-2" v-if="!content.dossier.isracine">
					<div class="font-size-sm text-muted flex-fill">{{ content.dossier.breadcrumb }}</div>
				</div>
				<ul class="list-group">
					<a href="#" v-if="!content.dossier.isracine" class="list-group-item list-group-item-action d-flex"
					   @click.prevent="goParent">
						<div><i class="fa fa-level-up-alt"></i></div>
						<div class="ml-3 flex-fill">{{ $t('Back to parent folder') }}</div>
					</a>
					<a href="#" v-for="dos in content.dossiers" :key="`dos-`+dos.id"
					   class="list-group-item list-group-item-action d-flex"
					   @click.prevent="openFolder(dos.id)">
						<div><display-extension></display-extension></div>
						<div class="ml-3 flex-fill">{{ dos.libelle }}</div>
					</a>
					<a href="#" v-for="fic in content.fichiers" :key="`fic-`+fic.id"
					   class="list-group-item list-group-item-action d-flex"
					   :class="{'disabled': isDownloading(fic.id)}"
					   @click.prevent="download(fic)">
						<div>
							<i class="fa fa-circle-notch fa-spin text-muted" v-if="isDownloading(fic.id)"></i>
							<display-extension :extension="fic.extension" v-else></display-extension>
						</div>
						<div class="ml-3 flex-fill">
							{{ fic.libelle }}
							<div class="font-size-sm text-muted">{{ fic.taillehumaine }}</div>
						</div>
					</a>
				</ul>
				<div class="text-center text-muted py-4"
					 v-if="!content.dossiers.length && !content.fichiers.length">
					{{ $t('No file in this folder') }}
				</div>
			</template>
		</template>
	</div>
</template>

<script>
	import {mapActions, mapGetters} from 'vuex'
	import { ADD_TOAST_MESSAGE } from 'vuex-toast'
	import api from '@/services/api'
	import DisplayExtension from '@/components/DisplayExtension.vue'

	export default {
		name: 'bibliotheque',
		components: {
			'display-extension': DisplayExtension
		},
		data() {
			return {
				query: '',
				timer: null,
				// pile des dossiers ouverts : sert au swipe, au bouton retour du
				// telephone et au lien "dossier parent"
				stack: [],
				popping: false,
				// ids des fichiers en cours de telechargement : le serveur peut
				// devoir les rapatrier depuis S3, ce qui prend quelques secondes
				downloading: []
			}
		},
		computed: {
			...mapGetters('config', {'config': 'getConfig'}),
			...mapGetters('bibliotheque', {
				'content': 'getContent',
				'search': 'getSearch',
				'loading': 'isLoading',
				'error': 'getError'
			}),
			isSearch() {
				return this.search !== null;
			}
		},
		created() {
			if (!this.config.usebibliotheque) {
				this.$router.replace({name: 'home'});
				return;
			}
			window.addEventListener('popstate', this.onPopState);
			this.refresh();
			this.loadContent(null);
		},
		beforeDestroy() {
			if (this.timer) {
				clearTimeout(this.timer);
			}
			window.removeEventListener('popstate', this.onPopState);
		},
		methods: {
			...mapActions('bibliotheque', ['loadContent', 'runSearch', 'clearSearch', 'refresh']),
			...mapActions({
				addToast: ADD_TOAST_MESSAGE
			}),
			onSearchInput() {
				let self = this;
				if (this.timer) {
					clearTimeout(this.timer);
				}
				if (this.query.trim().length < 2) {
					this.clearSearch();
					return;
				}
				this.timer = setTimeout(function () {
					self.runSearch(self.query.trim());
				}, 400);
			},
			resetSearch() {
				this.query = '';
				this.clearSearch();
			},
			// recharge le dossier courant depuis le serveur
			reload() {
				// la racine est cachee sous la cle '' : ne pas la recharger sous son id
				let id = (this.content && !this.content.dossier.isracine) ? this.content.dossier.id : null;
				this.query = '';
				this.clearSearch();
				this.refresh();
				this.loadContent(id);
			},
			openFolder(id) {
				this.query = '';
				this.clearSearch();
				// une entree d'historique par descente : le bouton retour du
				// telephone remonte alors d'un dossier au lieu de quitter l'ecran
				if (!this.popping) {
					this.stack.push(id || null);
					history.pushState({bibliotheque: this.stack.length}, '');
				}
				this.loadContent(id || null);
			},
			goParent() {
				if (this.isSearch) {
					this.resetSearch();
					return;
				}
				if (!this.content || this.content.dossier.isracine) {
					return;
				}
				// passe par l'historique pour rester synchronise avec la pile
				if (this.stack.length) {
					history.back();
				} else {
					this.loadContent(this.content.dossier.parentid || null);
				}
			},
			onPopState() {
				if (!this.stack.length) {
					return;
				}
				this.stack.pop();
				let parent = this.stack.length ? this.stack[this.stack.length - 1] : null;
				this.popping = true;
				this.query = '';
				this.clearSearch();
				this.loadContent(parent);
				this.popping = false;
			},
			isDownloading(id) {
				return this.downloading.indexOf(id) !== -1;
			},
			download(fic) {
				if (this.isDownloading(fic.id)) {
					return;
				}
				let self = this;
				this.downloading.push(fic.id);
				api.getFile({
					url: 'intervention/bibliotheque/fichiers/' + fic.id + '/get',
					filename: fic.libelle
				}).catch(function () {
					self.addToast({
						text: self.$t('Download error'),
						type: 'danger',
						dismissAfter: 5000
					});
				}).then(function () {
					self.downloading = self.downloading.filter(function (id) {
						return id !== fic.id;
					});
				});
			}
		}
	}
</script>
